import _ from "lodash";
import { Protection } from "@sportal/api";

import { INPUT_WHITESPACES_REGEX } from "../../../../components/input/input.helpers";
import { getMapList } from "./webFilters.selectors";
import {
  ChangeProtectionCategoriesFn,
  ProtectionCategoryWithAction,
  ProtectionCategoryDetails,
} from "./protectionEditor/protectionEditor.types";
import { ProtectionStatus } from "./protectionEditor/protectionEditor.constants";
import { CategoriesByStatus } from "./webFilters.types";

interface ProtectionStateDetails {
  id: ProtectionStatus;
  icon: string;
  actionName: string;
}

const listConfig: Record<ProtectionStatus, ProtectionStateDetails> = {
  blocked: {
    id: ProtectionStatus.Blocked,
    icon: "fa-ban color-danger",
    actionName: "onAllow",
  },
  allowed: {
    id: ProtectionStatus.Allowed,
    icon: "fa-check color-success",
    actionName: "onBlock",
  },
};

export const getDetailedCategories = (
  categoryList: ProtectionCategoryWithAction[],
  blockedByWebFilter: string[],
  protection: string,
  translate: (id: string, values?: {}) => string
): ProtectionCategoryDetails[] => {
  return _(categoryList)
    .map((category) => ({
      ...category,
      description: translate(`category_${category.name}.description`),
      warning: _.includes(blockedByWebFilter, category.name)
        ? translate(`blocked_by`, { protection: translate(protection) })
        : null,
      displayName: translate(`category_${category.name}`),
    }))
    .orderBy("displayName")
    .value();
};

export const getDetailedCategoriesWithActions = (
  categories: CategoriesByStatus,
  onBlock: ChangeProtectionCategoriesFn,
  onAllow: ChangeProtectionCategoriesFn,
  blockedByWebFilter: string[],
  protection: Protection,
  translate: (id: string, values?: {}) => string
): ProtectionCategoryDetails[] => {
  const blocked: ProtectionCategoryWithAction[] = getMapList(
    { categories, onBlock, onAllow },
    listConfig.blocked
  );
  const allowed: ProtectionCategoryWithAction[] = getMapList(
    { categories, onBlock, onAllow },
    listConfig.allowed
  );

  return getDetailedCategories(
    [...blocked, ...allowed],
    blockedByWebFilter,
    protection,
    translate
  );
};

const trimAndSanitizeFilter = (filter: string): string => {
  const trimmed = filter.trim();
  return _.escapeRegExp(trimmed.replace(INPUT_WHITESPACES_REGEX, " "));
};

const doesCategoryNameMatchFilter = (
  categoryName: string,
  filter: string
): boolean => {
  const filterRegex = new RegExp(trimAndSanitizeFilter(filter), "gi");
  return filterRegex.test(categoryName);
};

export const getCategoriesByName = (
  categories: ProtectionCategoryDetails[],
  filter: string
): ProtectionCategoryDetails[] =>
  categories.filter((category) =>
    doesCategoryNameMatchFilter(category.displayName, filter)
  );

export const getBlocked = <C extends ProtectionCategoryDetails>(
  category: C
): boolean => category.isBlocked;
export const getAllowed = <C extends ProtectionCategoryDetails>(
  category: C
): boolean => !category.isBlocked;

export const filterCategoriesByStatus = (
  categories: ProtectionCategoryDetails[],
  statusList: ProtectionStatus[]
): ProtectionCategoryDetails[] => {
  if (!statusList.length) {
    return [];
  }

  if (statusList.length === 2) {
    return categories;
  }

  if (statusList[0] === ProtectionStatus.Blocked) {
    return categories.filter(getBlocked);
  }

  if (statusList[0] === ProtectionStatus.Allowed) {
    return categories.filter(getAllowed);
  }
};
